import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOffline } from '../../contexts/OfflineContext';
import DownloadProgressModal from '../DownloadProgressModal';
import RemoveDownloadModal from '../RemoveDownloadModal';

// Hamburger menu icon for the dropdown trigger (styled gold via CSS)
const MenuIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="4" y1="6" x2="20" y2="6" />
    <line x1="4" y1="12" x2="20" y2="12" />
    <line x1="4" y1="18" x2="20" y2="18" />
  </svg>
);

const ShareIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="18" cy="5" r="3"/>
    <circle cx="6" cy="12" r="3"/>
    <circle cx="18" cy="19" r="3"/>
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
  </svg>
);

const MapIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
    <circle cx="12" cy="10" r="3"/>
  </svg>
);

const TripIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
    <line x1="16" y1="2" x2="16" y2="6"/>
    <line x1="8" y1="2" x2="8" y2="6"/>
    <line x1="3" y1="10" x2="21" y2="10"/>
    <polyline points="9 16 11 18 15 14"/>
  </svg>
);

const DownloadIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const DownloadedIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="9 11 12 14 22 4" />
  </svg>
);

const DownloadingIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="downloading-spinner">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <circle cx="12" cy="8" r="4" strokeDasharray="20" strokeDashoffset="5" />
  </svg>
);

const LockSmallIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

/**
 * PremiumActionsDropdown - Consolidates premium action buttons into a dropdown
 * Only renders if there are 2+ premium features available
 *
 * Props:
 * - listId: string
 * - listTitle: string
 * - list: object (for checking itinerary existence)
 * - shareToken: string|null
 * - canPublicShare: boolean
 * - canViewMap: boolean
 * - canTripPlanner: boolean
 * - canOfflineDownload: boolean
 * - hasInactiveShareToken: boolean
 * - hasInactiveItinerary: boolean
 * - onShare: function
 * - onTripPlan: function
 * - onReactivateFeature: function (called with feature name when inactive feature clicked)
 */
function PremiumActionsDropdown({
  listId,
  listTitle,
  list,
  shareToken,
  canPublicShare,
  canViewMap,
  canTripPlanner,
  canOfflineDownload,
  hasInactiveShareToken,
  hasInactiveItinerary,
  onShare,
  onTripPlan,
  onReactivateFeature,
}) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Download state from OfflineContext
  const {
    isListDownloaded,
    isListDownloading,
    getDownloadProgress,
    downloadList,
    cancelDownload,
    removeOfflineList,
  } = useOffline();

  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const isDownloaded = isListDownloaded(listId);
  const isDownloading = isListDownloading(listId);
  const progress = getDownloadProgress(listId);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Close on escape
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  // Count active premium features
  const activePremiumCount = [
    canPublicShare || hasInactiveShareToken,
    canTripPlanner || hasInactiveItinerary,
    canViewMap,
    canOfflineDownload,
  ].filter(Boolean).length;

  // Don't render if less than 2 premium features
  if (activePremiumCount < 2) {
    return null;
  }

  const handleShareClick = () => {
    setIsOpen(false);
    if (hasInactiveShareToken) {
      onReactivateFeature?.('share');
    } else {
      onShare();
    }
  };

  const handleTripClick = () => {
    setIsOpen(false);
    if (hasInactiveItinerary) {
      onReactivateFeature?.('trip');
    } else {
      onTripPlan();
    }
  };

  const handleMapClick = () => {
    setIsOpen(false);
    navigate(`/map?list=${listId}`);
  };

  const handleDownloadClick = async () => {
    if (!canOfflineDownload || isDownloading) {
      return;
    }

    if (isDownloaded) {
      setShowRemoveModal(true);
      setIsOpen(false);
    } else {
      setIsOpen(false);
      try {
        await downloadList(listId);
      } catch (err) {
        console.error('Failed to download list:', err);
      }
    }
  };

  const handleConfirmRemove = async () => {
    setIsRemoving(true);
    try {
      await removeOfflineList(listId);
      setShowRemoveModal(false);
    } catch (err) {
      console.error('Failed to remove offline list:', err);
    } finally {
      setIsRemoving(false);
    }
  };

  const handleCancelDownload = () => {
    cancelDownload(listId);
  };

  return (
    <>
      <div className="premium-actions-dropdown" ref={dropdownRef}>
        <button
          className={`btn-icon-header premium-dropdown-trigger ${isOpen ? 'active' : ''}`}
          onClick={() => setIsOpen(!isOpen)}
          title="Premium features"
          aria-expanded={isOpen}
          aria-haspopup="true"
        >
          <MenuIcon />
        </button>

        {isOpen && (
          <div className="premium-dropdown-menu">
            <div className="premium-dropdown-header">
              Premium Features
            </div>

            {/* Share */}
            {(canPublicShare || hasInactiveShareToken) && (
              <button
                className={`premium-dropdown-item ${shareToken ? 'has-indicator' : ''} ${hasInactiveShareToken ? 'inactive' : ''}`}
                onClick={handleShareClick}
              >
                <ShareIcon />
                <span className="item-label">
                  {shareToken ? 'Share Link' : 'Create Share Link'}
                </span>
                {shareToken && !hasInactiveShareToken && (
                  <span className="item-indicator active"><CheckIcon /></span>
                )}
                {hasInactiveShareToken && (
                  <span className="item-indicator locked"><LockSmallIcon /></span>
                )}
              </button>
            )}

            {/* Trip Planner */}
            {(canTripPlanner || hasInactiveItinerary) && (
              <button
                className={`premium-dropdown-item ${list?.itinerary ? 'has-indicator' : ''} ${hasInactiveItinerary ? 'inactive' : ''}`}
                onClick={handleTripClick}
              >
                <TripIcon />
                <span className="item-label">Trip Planner</span>
                {list?.itinerary && !hasInactiveItinerary && (
                  <span className="item-indicator active"><CheckIcon /></span>
                )}
                {hasInactiveItinerary && (
                  <span className="item-indicator locked"><LockSmallIcon /></span>
                )}
              </button>
            )}

            {/* Map View */}
            {canViewMap && (
              <button
                className="premium-dropdown-item"
                onClick={handleMapClick}
              >
                <MapIcon />
                <span className="item-label">View on Map</span>
              </button>
            )}

            {/* Download */}
            {canOfflineDownload && (
              <button
                className={`premium-dropdown-item ${isDownloaded ? 'has-indicator' : ''}`}
                onClick={handleDownloadClick}
                disabled={isDownloading}
              >
                {isDownloading ? <DownloadingIcon /> : isDownloaded ? <DownloadedIcon /> : <DownloadIcon />}
                <span className="item-label">
                  {isDownloading ? 'Saving...' : isDownloaded ? 'Saved Offline' : 'Save Offline'}
                </span>
                {isDownloaded && (
                  <span className="item-indicator active"><CheckIcon /></span>
                )}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Download progress modal */}
      <DownloadProgressModal
        isOpen={isDownloading}
        listTitle={listTitle || 'List'}
        phase={progress?.phase}
        current={progress?.current || 0}
        total={progress?.total || 0}
        onCancel={handleCancelDownload}
      />

      {/* Remove confirmation modal */}
      <RemoveDownloadModal
        isOpen={showRemoveModal}
        listTitle={listTitle || 'List'}
        onConfirm={handleConfirmRemove}
        onCancel={() => setShowRemoveModal(false)}
        isRemoving={isRemoving}
      />
    </>
  );
}

export default PremiumActionsDropdown;
